/**
 * The "Assigned" cell on every Enquiries table and card.
 *
 * Three states, and collapsing any two is a defect:
 * - `undefined` — the server did not report the field (an older API). An em
 *   dash, NEVER "Unassigned": nobody looked, so nothing may be claimed.
 * - `null` — unassigned, said in words.
 * - an assignment — who assigned it to whom, and when.
 *
 * ⚠️ The names are the SNAPSHOT taken at assignment time, so a person since
 * removed from the Settings list still renders, marked "(removed)".
 *
 * Built to truncate in a ~200px cell; the full sentence, the assignee's email
 * and team are on the `title`.
 */

import { formatDateTime } from '@/lib/format';
import type { EnquiryAssignment } from '@/lib/types';

export interface EnquiryAssignmentCellProps {
  assignment: EnquiryAssignment | null | undefined;
}

/** "<X> assigned it to <Y> on <date time>", plus email and team — the full fact. */
function enquiryAssignmentSentence(assignment: EnquiryAssignment): string {
  const by = assignment.assignedBy.name?.trim() || 'Someone';
  const removed = assignment.assignee.active ? '' : ' (removed)';
  const team = assignment.assignee.team?.trim() ?? '';
  const contact = [assignment.assignee.email, team].filter((part) => part !== '').join(', ');
  return `${by} assigned it to ${assignment.assignee.name}${removed} on ${formatDateTime(
    assignment.assignedAt,
  )}${contact === '' ? '' : ` (${contact})`}`;
}

export function EnquiryAssignmentCell({ assignment }: EnquiryAssignmentCellProps) {
  if (assignment === undefined) {
    return <span className="text-fg-muted">—</span>;
  }
  if (assignment === null) {
    return <span className="text-fg-muted">Unassigned</span>;
  }

  const by = assignment.assignedBy.name?.trim() || 'Someone';
  const date = new Date(assignment.assignedAt).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  return (
    <span className="block min-w-0" title={enquiryAssignmentSentence(assignment)}>
      <span className="block truncate text-fg">
        <span className="text-fg-secondary">{by}</span>
        {' → '}
        <span className="font-medium">{assignment.assignee.name}</span>
        {assignment.assignee.active ? null : <span className="text-fg-muted"> (removed)</span>}
      </span>
      <span className="block truncate text-xs text-fg-muted tabular-nums">{date}</span>
    </span>
  );
}
